import React, { useState } from 'react';
import { CARD } from '../ui';
import { DATA_HUE, TRACK } from '../dashboard/theme';
import { LEADERBOARD_METRICS } from './overview';
import { AGING_COLORS, PLAN_COLORS } from './theme';
import { formatMoney, plural } from './format';

// A row of mutually exclusive choices (chart breakdown, leaderboard metric).
export const SegmentedControl = ({ label, options, value, onChange }) => (
  <div role="group" aria-label={label} className="inline-flex rounded-lg border border-gray-600 bg-gray-900/40 p-0.5">
    {options.map((option) => {
      const active = option.id === value;
      return (
        <button
          key={option.id}
          type="button"
          onClick={() => onChange(option.id)}
          aria-pressed={active}
          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
            active ? 'bg-purple-500/25 hover:bg-purple-500/30 text-purple-100' : 'bg-transparent hover:bg-gray-700/60 text-gray-400 hover:text-white'
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

// One clickable ranked row: label, value and a bar on a shared scale. When any
// row in the list is selected the rest dim, so the selection reads at a glance
// while the others stay one click away.
const BarRow = ({ label, sublabel, cents, max, color, selected, anySelected, onClick, marker }) => (
  <li>
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`w-full text-left rounded-lg px-2 py-2 -mx-2 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 ${
        selected ? 'bg-purple-500/15 hover:bg-purple-500/20' : 'bg-transparent hover:bg-gray-700/40'
      } ${anySelected && !selected ? 'opacity-50 hover:opacity-90' : ''}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex items-center gap-2 min-w-0">
          {marker}
          <span className="text-sm text-gray-200 truncate">{label}</span>
        </span>
        <span className="text-sm font-semibold text-white tabular-nums flex-shrink-0">{formatMoney(cents)}</span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-sm overflow-hidden" style={{ backgroundColor: TRACK }}>
        <div className="h-full" style={{ width: `${max ? (cents / max) * 100 : 0}%`, backgroundColor: color, borderRadius: '0 4px 4px 0' }} />
      </div>
      {sublabel && <p className="mt-1 text-xs text-gray-500 truncate">{sublabel}</p>}
    </button>
  </li>
);

const Dot = ({ color }) => (
  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} aria-hidden="true" />
);

export const AgingPanel = ({ buckets, selected, onSelect }) => {
  const total = buckets.reduce((sum, b) => sum + b.cents, 0);
  const max = Math.max(1, ...buckets.map((b) => b.cents));
  return (
    <div className={`${CARD} p-6`}>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-white">Receivables</h2>
        <p className="text-sm text-gray-500 mt-1">Open balances by how late they are, as of today. Click one to list its invoices.</p>
      </div>
      {total === 0 ? (
        <p className="py-8 text-center text-sm text-emerald-300">Nothing is owed in this slice.</p>
      ) : (
        <ul className="space-y-1">
          {buckets.map((bucket) => (
            <BarRow
              key={bucket.id}
              label={bucket.label}
              sublabel={bucket.invoices ? `${plural(bucket.invoices, 'invoice')} · ${plural(bucket.companies, 'company', 'companies')}` : 'None'}
              cents={bucket.cents}
              max={max}
              color={AGING_COLORS[bucket.id]}
              marker={<Dot color={AGING_COLORS[bucket.id]} />}
              selected={selected === bucket.id}
              anySelected={Boolean(selected)}
              onClick={() => onSelect(selected === bucket.id ? null : bucket.id)}
            />
          ))}
        </ul>
      )}
      <div className="mt-4 pt-3 border-t border-gray-700/60 flex justify-between text-sm">
        <span className="text-gray-400">Total outstanding</span>
        <span className="text-white font-semibold tabular-nums">{formatMoney(total)}</span>
      </div>
    </div>
  );
};

const LIMIT = 7;

export const CompanyLeaderboard = ({ rows, metric, onMetric, metricLocked, selected, onToggle }) => {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? rows : rows.slice(0, LIMIT);
  const max = Math.max(1, ...rows.map((r) => r.cents));
  const metricLabel = LEADERBOARD_METRICS.find((m) => m.id === metric)?.label.toLowerCase();
  return (
    <div className={`${CARD} p-6`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-white">Customers</h2>
          <p className="text-sm text-gray-500 mt-1">Click a company to filter the page to it.</p>
        </div>
        {metricLocked ? (
          <span className="text-xs text-gray-400 border border-gray-600 rounded-lg px-2.5 py-1.5">Owed</span>
        ) : (
          <SegmentedControl label="Rank customers by" options={LEADERBOARD_METRICS} value={metric} onChange={onMetric} />
        )}
      </div>
      {rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500">Nothing {metricLabel} in this slice.</p>
      ) : (
        <ul className="space-y-1">
          {shown.map((row) => (
            <BarRow
              key={row.id}
              label={row.name}
              cents={row.cents}
              max={max}
              color={DATA_HUE}
              selected={selected.includes(row.id)}
              anySelected={selected.length > 0}
              onClick={() => onToggle(row.id)}
            />
          ))}
        </ul>
      )}
      {rows.length > LIMIT && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-3 bg-transparent hover:bg-gray-700/40 text-purple-300 hover:text-purple-200 px-2 py-1 -mx-2 rounded text-sm"
        >
          {showAll ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
};

export const PlanMix = ({ rows, selected, onToggle }) => {
  const total = rows.reduce((sum, r) => sum + r.cents, 0);
  const max = Math.max(1, ...rows.map((r) => r.cents));
  return (
    <div className={`${CARD} p-6`}>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-white">Revenue by plan</h2>
        <p className="text-sm text-gray-500 mt-1">Billed in this slice. Click a plan to filter the page to it.</p>
      </div>
      <ul className="space-y-1">
        {rows.map((row) => (
          <BarRow
            key={row.id}
            label={row.label}
            sublabel={`${total ? Math.round((row.cents / total) * 100) : 0}% of billed · ${plural(row.companies, 'company', 'companies')}`}
            cents={row.cents}
            max={max}
            color={PLAN_COLORS[row.id]}
            marker={<span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: PLAN_COLORS[row.id] }} aria-hidden="true" />}
            selected={selected.includes(row.id)}
            anySelected={selected.length > 0}
            onClick={() => onToggle(row.id)}
          />
        ))}
      </ul>
    </div>
  );
};
